import { useMemo } from 'react';
import { sumMoney, calculateItemTaxAmount, calculateItemDiscountAmount, calculateTotalOverallDiscountAmount, truncateToTwoDecimals } from '../utils';

/**
 * Custom hook for managing all invoice financial calculations
 * 
 * This hook centralizes all the complex financial calculations for an invoice,
 * including subtotals, taxes, discounts, payments, and balances.
 * 
 * @param {Object} invoiceState - The invoice state containing items, payments, discounts, etc.
 * @param {Array} refundsData - Array of refund records
 * @returns {Object} All calculated financial values for the invoice
 */
export const useInvoiceCalculations = ( invoiceState ) => {
    // Ensure invoiceState is an object and provide defaults for all properties
    const { invoiceItems = [], payments = [], discounts = [], claims = [], status = '' } = invoiceState;
    const isReturnInvoice = String(status).toLowerCase() === 'refund';

    // Filter out items marked for deletion
    const activeInvoiceItems = useMemo(() => 
        invoiceItems.filter(item => !item._markedForDeletion),
        [invoiceItems]
    );

    const activePayments = useMemo(() =>
        payments.filter(payment => !payment._markedForDeletion),
        [payments]
    );

    const activeDiscounts = useMemo(() =>
        discounts.filter(discount => !discount._markedForDeletion),
        [discounts]
    );

    const activeClaims = useMemo(() =>
        claims.filter(claim => !claim._markedForDeletion),
        [claims]
    );

    // Calculate line items subtotal (before any discounts or taxes)
    const lineItemsSubtotal = useMemo(() => 
        sumMoney(activeInvoiceItems.map(item => (item.quantity || 0) * (item.price || 0))), 
        [activeInvoiceItems]
    );

    // Calculate total tax amount from all line items
    const totalItemTaxAmount = useMemo(() => 
        sumMoney(activeInvoiceItems.map(calculateItemTaxAmount)), 
        [activeInvoiceItems]
    );

    // Calculate total discount amount from line items
    const totalItemDiscountAmount = useMemo(() => 
        sumMoney(activeInvoiceItems.map(item => {
            const value = calculateItemDiscountAmount(item);
            // Return invoices are already persisted with signed totals; avoid negative
            // discount artifacts from percentage formulas on negative line subtotals.
            return isReturnInvoice ? Math.max(0, value) : value;
        })), 
        [activeInvoiceItems, isReturnInvoice]
    );

    // Calculate overall discount amount (from discount records)
    const totalOverallDiscountAmount = useMemo(() => {
        if (isReturnInvoice) {
            // Return invoices persist reversed overall discounts in appliedAmount;
            // use that signed value directly so mirrored totals settle correctly.
            return sumMoney(activeDiscounts.map(discount => Number(discount.appliedAmount || 0)));
        }
        const result = calculateTotalOverallDiscountAmount(
            activeDiscounts,
            lineItemsSubtotal
        );
        return result;
    }, [lineItemsSubtotal, activeDiscounts, isReturnInvoice]);

    // Calculate total discount (line items + overall discounts)
    const totalDiscount = useMemo(() => 
        totalItemDiscountAmount + totalOverallDiscountAmount, 
        [totalItemDiscountAmount, totalOverallDiscountAmount]
    );

    // Calculate tax on discounted amount
    /*const taxOnDiscountedAmount = useMemo(() => {
        const subtotalAfterDiscount = lineItemsSubtotal - totalDiscount;
        // For simplicity, assuming a standard tax rate on the discounted amount
        // This should be configurable based on business rules
        const taxRate = 0; // Adjust based on business requirements
        return subtotalAfterDiscount * taxRate;
    }, [lineItemsSubtotal, totalDiscount]);*/

    /**
     * Memoized total tax amount applied to invoice line items after item-level discounts
     * and a prorated overall invoice discount have been deducted.
     *
     * The constant is the sum of taxForItem for all invoiceItems and is memoized using
     * invoiceItems, lineItemsSubtotal, and totalOverallDiscountAmount as dependencies.
     *
     * @constant
     * @type {number}
     * @readonly
     * @returns {number} Total tax amount for the invoice (in the same currency/unit as prices)
     */
    const taxOnDiscountedAmount = useMemo(() => {
        if (isReturnInvoice) {
            return sumMoney(activeInvoiceItems.map(item => item.tax || 0));
        }

        return sumMoney(activeInvoiceItems.map(item => {
            const itemSubtotal = (item.quantity || 1) * (item.price || 0);
            const itemDiscountAmount = (item.discount || 0) / 100 * itemSubtotal;
            const itemShare = itemSubtotal > 0 ? itemSubtotal / lineItemsSubtotal : 0;
            const itemOverallDiscount = totalOverallDiscountAmount * itemShare;
            const itemDiscountedAmount = itemSubtotal - itemDiscountAmount - itemOverallDiscount;
            return (item.tax || 0) / 100 * Math.max(0, itemDiscountedAmount);
        }));
    }, [activeInvoiceItems, lineItemsSubtotal, totalOverallDiscountAmount, isReturnInvoice]);

    // Calculate total amount due (subtotal - discounts + taxes)
    const totalDue = useMemo(() => {
        if (isReturnInvoice) {
            const itemTotals = sumMoney(activeInvoiceItems.map(item => item.total || 0));
            return truncateToTwoDecimals(itemTotals - totalOverallDiscountAmount);
        }

        const result = lineItemsSubtotal - totalDiscount + taxOnDiscountedAmount;
        return truncateToTwoDecimals(Math.max(0, result)); // Ensure non-negative
    }, [lineItemsSubtotal, totalDiscount, taxOnDiscountedAmount, activeInvoiceItems, isReturnInvoice, totalOverallDiscountAmount]);

    // Calculate total customer payments
    const totalCustomerPaid = useMemo(() => 
        sumMoney(activePayments.map(payment => payment.paymentAmount || 0)), 
        [activePayments]
    );

    // Calculate total insurance approved amounts
    const totalInsuranceApproved = useMemo(() => {
        return sumMoney(activeClaims.map(claim => {
            // Only count approved claims and reversed claims with an approved amount, reversed claims are refunds.
            if ((claim.status.toLowerCase() === 'approved' || claim.status.toLowerCase() === 'reversed') && claim.approvedAmount) {
                return claim.approvedAmount;
            }
            return 0;
        }));
    }, [activeClaims]);

    // Calculate total refunded amount
    const totalRefunded = useMemo(() => {
        if (!invoiceState.refunds || invoiceState.refunds.length === 0) return 0;
        return sumMoney(invoiceState.refunds
            .filter(refund => refund.status.toLowerCase() === 'approved')
            .map(refund => refund.refundAmount || 0)
        );
    }, [invoiceState.refunds]);

    // Calculate maximum refundable amount
    const maxRefundAmount = useMemo(() => {
        // Can't refund more than what was actually collected minus what was already refunded
        return Math.max(0, totalCustomerPaid - totalRefunded);
    }, [totalCustomerPaid, totalRefunded]);

    // Calculate total collected (customer payments + insurance)
    const totalCollected = useMemo(() => 
        totalCustomerPaid + totalInsuranceApproved, 
        [totalCustomerPaid, totalInsuranceApproved]
    );

    // Calculate remaining balance due
    // Note: Refunds increase the balance (money returned to customer means they've effectively paid less)
    const balanceDue = useMemo(() => {
        const balance = truncateToTwoDecimals(totalDue) - 
                       truncateToTwoDecimals(totalCollected) //+ 
                       //truncateToTwoDecimals(totalRefunded);
        return truncateToTwoDecimals(isReturnInvoice ? balance : Math.max(0, balance));
    }, [totalDue, totalCollected, /*totalRefunded,*/ isReturnInvoice]);

    // Calculate if invoice is paid in full
    const isPaidInFull = useMemo(() => 
        balanceDue <= 0.01, // Allow for minor rounding differences
        [balanceDue]
    );

    // Calculate payment status
    const paymentStatus = useMemo(() => {
        if (isPaidInFull) return 'paid';
        if (totalCollected > 0) return 'partial';
        return 'unpaid';
    }, [isPaidInFull, totalCollected]);

    return {
        // Subtotals and base amounts
        lineItemsSubtotal,
        totalItemTaxAmount,
        totalItemDiscountAmount,
        totalDiscount,
        totalDue,
        
        // Payment and collection amounts
        totalOverallDiscountAmount,        
        taxOnDiscountedAmount,        
        totalCustomerPaid,
        totalInsuranceApproved,        
        totalRefunded,
        maxRefundAmount,
        totalCollected,
        
        // Balance and status
        balanceDue,
        isPaidInFull,
        paymentStatus
    };
};

export default useInvoiceCalculations;
